import * as THREE from 'three'
import type { FpsBots } from './FpsBots.js'
import type { FpsStorm } from './FpsStorm.js'
import type { FpsWeaponPickups } from './FpsWeaponPickups.js'
import type { FpsHealItems } from './FpsHealItems.js'
import type { BotDifficultyTier } from './FpsSettingsMenu.js'
import { rarityRank, type WeaponRarity } from './FpsWeaponRarity.js'

export type BotDifficulty = 'rookie' | 'standard' | 'elite'

export interface BotAI2Options {
  difficulty: BotDifficulty
}

interface BotAI2State {
  botIndex: number
  coverTarget: THREE.Vector3 | null
  coverSearchCd: number
  panicBuildCd: number
  squadPingCd: number
  harvestCd: number
  lastDamageT: number
  isPanicBuilding: boolean
  isSeeking: boolean
  lootTarget: THREE.Vector3 | null
  lootCd: number
  healCalloutCd: number
}

/** Temporary panic-wall mesh tracked for auto-removal */
interface PanicWall {
  mesh: THREE.Mesh
  ttl: number
}

const BOT_LOOT_RANGE = 8
const BOT_HEAL_HP_THRESHOLD = 50
const EARLY_STORM_ROTATE_ETA = 30

const TIER_TO_DIFF: Record<BotDifficultyTier, BotDifficulty> = {
  casual: 'rookie',
  normal: 'standard',
  sweaty: 'elite',
}

export class FpsBotAI2 {
  private states: BotAI2State[] = []
  private panicWalls: PanicWall[] = []
  private endgameMode = false
  private options: BotAI2Options

  private readonly diffReactionTime: Record<BotDifficulty, number> = {
    rookie: 0.8,
    standard: 0.45,
    elite: 0.18,
  }
  private readonly diffHitBonus: Record<BotDifficulty, number> = {
    rookie: -0.06,
    standard: 0,
    elite: 0.12,
  }

  constructor(
    private bots: FpsBots,
    private scene: THREE.Scene,
    options: BotAI2Options = { difficulty: 'standard' },
  ) {
    this.options = options
  }

  initialize(botCount: number): void {
    this.states = []
    for (let i = 0; i < botCount; i++) {
      this.states.push(this.makeState(i))
    }
  }

  setDifficultyTier(tier: BotDifficultyTier): void {
    this.options.difficulty = TIER_TO_DIFF[tier]
  }

  setEndgameMode(active: boolean): void {
    this.endgameMode = active
  }

  private makeState(botIndex: number): BotAI2State {
    return {
      botIndex,
      coverTarget: null,
      coverSearchCd: 0,
      panicBuildCd: 0,
      squadPingCd: 1.0 + Math.random() * 1.5,
      harvestCd: Math.random() * 4,
      lastDamageT: 999,
      isPanicBuilding: false,
      isSeeking: false,
      lootTarget: null,
      lootCd: Math.random() * 2,
      healCalloutCd: 0,
    }
  }

  /** Call when a bot takes damage (by living-bot index). */
  onBotDamaged(botIndex: number): void {
    if (botIndex < 0 || botIndex >= this.states.length) return
    const s = this.states[botIndex]!
    s.lastDamageT = 0
    s.coverSearchCd = 0
  }

  /** Convenience: find bot by name and mark as damaged. */
  onBotDamagedByName(botName: string): void {
    const liveBots = this.bots.getLivingBots()
    const idx = liveBots.findIndex((b) => b.name === botName)
    if (idx !== -1) this.onBotDamaged(idx)
  }

  /** Main per-frame update — call AFTER bots.update(). */
  update(
    dt: number,
    playerPos: THREE.Vector3,
    _elapsedMatchTime: number,
    ctx?: {
      storm?: FpsStorm
      weaponPickups?: FpsWeaponPickups
      healItems?: FpsHealItems
    },
  ): void {
    const diff = this.options.difficulty
    const liveBots = this.bots.getLivingBots()

    while (this.states.length < liveBots.length) {
      this.states.push(this.makeState(this.states.length))
    }

    const shrinkEta = ctx?.storm?.getShrinkEtaSeconds() ?? null
    const earlyStormRotate = shrinkEta != null && shrinkEta > 0 && shrinkEta < EARLY_STORM_ROTATE_ETA
    const nextStorm = earlyStormRotate ? ctx?.storm?.getNextCenterXZ() : null

    for (let i = 0; i < liveBots.length; i++) {
      const bot = liveBots[i]!
      const s = this.states[i]!

      s.lastDamageT += dt
      s.coverSearchCd = Math.max(0, s.coverSearchCd - dt)
      s.panicBuildCd = Math.max(0, s.panicBuildCd - dt)
      s.squadPingCd = Math.max(0, s.squadPingCd - dt)
      s.harvestCd = Math.max(0, s.harvestCd - dt)
      s.lootCd = Math.max(0, s.lootCd - dt)
      s.healCalloutCd = Math.max(0, s.healCalloutCd - dt)

      const botPos = new THREE.Vector3(bot.x, bot.y, bot.z)
      const inCombat = s.lastDamageT < 3.0

      if (inCombat && s.coverSearchCd <= 0) {
        s.coverTarget = this.findCoverPosition(botPos, playerPos)
        s.coverSearchCd = this.endgameMode ? 0.95 : 1.2
        this.bots.setBotCoverTarget(i, s.coverTarget)
        s.isSeeking = true
      }
      if (!inCombat) {
        s.coverTarget = null
        s.isSeeking = false
      }

      if (diff !== 'rookie') {
        const panicHpThreshold = (diff === 'elite' ? 60 : 40) + (this.endgameMode ? 8 : 0)
        if (bot.hp < panicHpThreshold && s.lastDamageT < 2.0 && s.panicBuildCd <= 0) {
          this.bots.triggerBotPanicBuild(i, playerPos)
          s.isPanicBuilding = true
          s.panicBuildCd = this.endgameMode ? 3.4 : 3.0
        }
      }

      if (diff !== 'rookie' && s.squadPingCd <= 0) {
        const distToPlayer = botPos.distanceTo(playerPos)
        if (distToPlayer < 26) {
          for (let j = 0; j < liveBots.length; j++) {
            if (j === i) continue
            const other = liveBots[j]!
            const otherPos = new THREE.Vector3(other.x, other.y, other.z)
            if (botPos.distanceTo(otherPos) < 18) {
              const os = this.states[j]!
              os.coverSearchCd = Math.max(0, os.coverSearchCd - 1.0)
            }
          }
        }
        s.squadPingCd = 2.5
      }

      // ── Loot priority: heal when low HP, upgrade weapon if better nearby ──
      s.lootTarget = null
      let moveOverrideSet = false
      if (!inCombat && s.lootCd <= 0) {
        const lootResult = this.tryBotLootPriority(
          i,
          bot,
          botPos,
          ctx?.weaponPickups,
          ctx?.healItems,
        )
        if (lootResult?.target) {
          s.lootTarget = lootResult.target
          this.bots.setBotMoveOverride(i, lootResult.target.x, lootResult.target.z)
          moveOverrideSet = true
          if (lootResult.healed && s.healCalloutCd <= 0) {
            this.bots.emitSquadTacticalCallout(bot.name, 'heal')
            s.healCalloutCd = 8
          }
        }
        s.lootCd = lootResult?.consumed ? 0.35 : 1.1
      }

      // ── Early storm rotation when shrink timer < 30s ──
      if (!moveOverrideSet && earlyStormRotate && nextStorm && !inCombat) {
        this.bots.setBotMoveOverride(i, nextStorm.x, nextStorm.z, true)
        moveOverrideSet = true
      }
      if (!moveOverrideSet) {
        this.bots.clearBotMoveOverride(i)
      }

      if (!inCombat && s.lastDamageT > 8 && s.harvestCd <= 0) {
        s.harvestCd = 4.0
      }
    }

    this.tickPanicWalls(dt)
  }

  getReactionTime(): number {
    return this.diffReactionTime[this.options.difficulty]
  }

  getHitBonus(): number {
    return this.diffHitBonus[this.options.difficulty]
  }

  dispose(): void {
    for (const pw of this.panicWalls) {
      this.scene.remove(pw.mesh)
      pw.mesh.geometry.dispose()
      ;(pw.mesh.material as THREE.Material).dispose()
    }
    this.panicWalls = []
    this.states = []
  }

  private tryBotLootPriority(
    botIndex: number,
    bot: { id: number; name: string; hp: number; weaponRarity: WeaponRarity; loadout: string },
    botPos: THREE.Vector3,
    weaponPickups?: FpsWeaponPickups,
    healItems?: FpsHealItems,
  ): { target?: THREE.Vector3; consumed?: boolean; healed?: boolean } | null {
    if (bot.hp < BOT_HEAL_HP_THRESHOLD && healItems) {
      const healPickup = healItems.findNearestPickup(botPos.x, botPos.z, BOT_LOOT_RANGE)
      if (healPickup) {
        const dist = Math.hypot(healPickup.x - botPos.x, healPickup.z - botPos.z)
        if (dist <= 2.2) {
          const restored = healItems.botConsumeAt(botPos.x, botPos.z, 2.2)
          if (restored > 0) {
            this.bots.healBotByIndex(botIndex, restored)
            return { consumed: true, healed: true }
          }
        }
        return { target: new THREE.Vector3(healPickup.x, 0, healPickup.z) }
      }
    }

    if (weaponPickups) {
      const weaponPickup = weaponPickups.findNearestPickup(botPos.x, botPos.z, BOT_LOOT_RANGE)
      if (weaponPickup && rarityRank(weaponPickup.rarity) > rarityRank(bot.weaponRarity)) {
        const dist = Math.hypot(weaponPickup.x - botPos.x, weaponPickup.z - botPos.z)
        if (dist <= 1.6) {
          const collected = weaponPickups.botTryCollectAt(botPos.x, botPos.z, 1.6)
          if (collected) {
            this.bots.upgradeBotWeaponByIndex(botIndex, collected.kind, collected.rarity)
            return { consumed: true }
          }
        }
        return { target: new THREE.Vector3(weaponPickup.x, 0, weaponPickup.z) }
      }
    }

    return null
  }

  private findCoverPosition(botPos: THREE.Vector3, playerPos: THREE.Vector3): THREE.Vector3 {
    const awayDir = new THREE.Vector3()
      .subVectors(botPos, playerPos)
      .setY(0)
      .normalize()

    let bestScore = -Infinity
    let bestPos = botPos.clone().addScaledVector(awayDir, 5)

    for (let k = 0; k < 8; k++) {
      const angle = (k / 8) * Math.PI * 2
      const dir = new THREE.Vector3(Math.cos(angle), 0, Math.sin(angle))
      const dot = dir.dot(awayDir)
      const radius = 4 + Math.random() * 4
      const candidate = botPos.clone().addScaledVector(dir, radius)

      if (dot > bestScore) {
        bestScore = dot
        bestPos = candidate
      }
    }
    return bestPos
  }

  private spawnPanicWall(botPos: THREE.Vector3, playerPos: THREE.Vector3): void {
    const dir = new THREE.Vector3()
      .subVectors(playerPos, botPos)
      .setY(0)
      .normalize()

    const wallPos = botPos.clone().addScaledVector(dir, 1.5)
    wallPos.y = botPos.y + 1.5

    const geo = new THREE.BoxGeometry(3, 3, 0.15)
    const mat = new THREE.MeshToonMaterial({
      color: 0xc4883e,
      transparent: true,
      opacity: 0.8,
    })
    const mesh = new THREE.Mesh(geo, mat)
    mesh.position.copy(wallPos)

    const angle = Math.atan2(dir.x, dir.z)
    mesh.rotation.y = angle

    this.scene.add(mesh)
    this.panicWalls.push({ mesh, ttl: 2.0 })
  }

  private tickPanicWalls(dt: number): void {
    for (let i = this.panicWalls.length - 1; i >= 0; i--) {
      const pw = this.panicWalls[i]!
      pw.ttl -= dt
      if (pw.ttl < 0.5) {
        const mat = pw.mesh.material as THREE.MeshToonMaterial
        mat.opacity = Math.max(0, pw.ttl / 0.5) * 0.8
      }
      if (pw.ttl <= 0) {
        this.scene.remove(pw.mesh)
        pw.mesh.geometry.dispose()
        ;(pw.mesh.material as THREE.Material).dispose()
        this.panicWalls.splice(i, 1)
      }
    }
  }
}
